import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Code2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import { CONTACT_EMAIL, CALENDLY_URL } from "@/content";

const DEFAULTS = {
  name: "Alistair Reid",
  title: "Founder",
  mobile: "07971 166789",
  linkedin: "https://www.linkedin.com/in/alistair-reid-aa868942/",
  website: "https://thrive-businessdevelopment.com",
};

const FIELDS = [
  ["name", "Name"],
  ["title", "Job title"],
  ["mobile", "Mobile"],
  ["linkedin", "LinkedIn URL"],
  ["website", "Website"],
];

const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function buildSignatureHtml(v, origin) {
  const font = "font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;";
  const site = v.website.replace(/^https?:\/\//, "").replace(/\/$/, "");
  const row = (label, inner) =>
    `<tr><td style="${font}font-size:12px;line-height:20px;color:#64748B;padding:0 12px 0 0;white-space:nowrap">${label}</td><td style="${font}font-size:12px;line-height:20px;color:#0B0F17">${inner}</td></tr>`;
  return (
    `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;${font}">` +
    `<tr><td style="padding:0 0 14px 0"><img src="${origin}/brand/thrive-logo-email.png" width="150" alt="Thrive Business Development" style="display:block;width:150px;height:auto;border:0"></td></tr>` +
    `<tr><td style="border-top:2px solid #D4AF37;padding:12px 0 0 0">` +
    `<p style="margin:0;${font}font-size:15px;line-height:22px;font-weight:700;color:#0B0F17">${esc(v.name)}</p>` +
    `<p style="margin:0 0 10px 0;${font}font-size:12px;line-height:18px;letter-spacing:1.5px;text-transform:uppercase;color:#B08D2A">${esc(v.title)}</p>` +
    `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse">` +
    (v.mobile ? row("M", `<a href="tel:${esc(v.mobile.replace(/\s+/g, ""))}" style="color:#0B0F17;text-decoration:none">${esc(v.mobile)}</a>`) : "") +
    row("E", `<a href="mailto:${CONTACT_EMAIL}" style="color:#0B0F17;text-decoration:none">${CONTACT_EMAIL}</a>`) +
    row("W", `<a href="${esc(v.website)}" style="color:#0B0F17;text-decoration:none">${esc(site)}</a>`) +
    (v.linkedin ? row("in", `<a href="${esc(v.linkedin)}" style="color:#0B0F17;text-decoration:none">LinkedIn</a>`) : "") +
    `</table>` +
    `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin-top:14px"><tr><td style="background:#D4AF37;padding:9px 16px">` +
    `<a href="${CALENDLY_URL}" target="_blank" style="${font}font-size:11px;line-height:14px;letter-spacing:2px;text-transform:uppercase;font-weight:700;color:#0B0F17;text-decoration:none">Book a Call</a>` +
    `</td></tr></table>` +
    `<p style="margin:14px 0 0 0;${font}font-size:11px;line-height:16px;color:#94A3B8">Strategic business development and market intelligence for energy and technical markets.</p>` +
    `</td></tr></table>`
  );
}

export default function SignatureGenerator() {
  const [v, setV] = useState(DEFAULTS);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const html = useMemo(() => buildSignatureHtml(v, origin), [v, origin]);

  const copyRich = async () => {
    try {
      const plain = `${v.name}\n${v.title}, Thrive Business Development\n${v.mobile}\n${CONTACT_EMAIL}\n${v.website}\nBook a call: ${CALENDLY_URL}`;
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
      toast.success("Signature copied", { description: "Paste into Gmail or Outlook signature settings." });
    } catch {
      toast.error("Copy not supported in this browser. Use Copy HTML instead.");
    }
  };

  const copyHtml = async () => {
    await navigator.clipboard.writeText(html);
    toast.success("HTML copied");
  };

  const btn =
    "inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 transition-colors duration-300 hover:border-gold/60 hover:text-gold";

  return (
    <div className="mt-20 border-t border-white/10 pt-14" data-testid="signature-generator">
      <Reveal>
        <p className="eyebrow mb-4">Email Signature</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-white">Signature Generator</h2>
        <p className="mt-4 max-w-2xl text-sm text-slate-400 leading-relaxed">
          Fill in your details, then use Copy Signature and paste directly into Gmail or Outlook. The
          logo is hosted from this website so it renders on white email backgrounds.
        </p>
      </Reveal>

      <div className="mt-10 grid gap-10 lg:grid-cols-[340px_1fr] lg:gap-16">
        <Reveal>
          <form className="flex flex-col gap-5" onSubmit={(e) => e.preventDefault()}>
            {FIELDS.map(([key, label]) => (
              <div key={key}>
                <label htmlFor={`sig-${key}`} className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">
                  {label}
                </label>
                <input
                  id={`sig-${key}`}
                  data-testid={`signature-${key}-input`}
                  value={v[key]}
                  onChange={(e) => setV({ ...v, [key]: e.target.value })}
                  className="w-full border border-white/15 bg-transparent px-4 py-3 text-sm text-white outline-none transition-colors focus:border-gold/60"
                />
              </div>
            ))}
            <div className="mt-2 flex flex-wrap gap-3">
              <button type="button" onClick={copyRich} data-testid="signature-copy-button" className="inline-flex items-center gap-2 bg-gold px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-gold-glow">
                <Copy size={13} /> Copy Signature
              </button>
              <button type="button" onClick={copyHtml} data-testid="signature-copy-html-button" className={btn}>
                <Code2 size={13} /> Copy HTML
              </button>
            </div>
          </form>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="border border-white/10 bg-[#F4F6F9] p-8 sm:p-10" data-testid="signature-preview">
            <div dangerouslySetInnerHTML={{ __html: html }} />
          </div>
          <p className="mt-4 font-mono text-[11px] leading-relaxed tracking-wider text-slate-500">
            Preview on a white background as it appears in most email clients.
          </p>
        </Reveal>
      </div>
    </div>
  );
}
